import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { adapter } from "@/services/db";
import { fmtTs } from "@/lib/format";
import { suspendAccount, reinstateAccount, isAccountSuspended } from "@/services/suspension";

const authToast = (r, verb) => (r.auth_enforced
  ? toast.success(`Account ${verb} — Firebase sign-in ${verb === "suspended" ? "disabled, sessions revoked" : "re-enabled"}`)
  : toast.warning(`Account ${verb} in Firestore. Sign-in NOT changed: ${r.reason || "service account missing"}`));
const errText = (e) => (e?.code === "permission-denied" ? "Permission denied — publish the updated Firestore rules" : e?.response?.data?.detail || e?.message);

// Suspend (2 remarks) / Reinstate (profile password) block + suspension history. Admin only.
export function AccountSuspension({ kind, entity, uid, phone, name }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [userMsg, setUserMsg] = useState("");
  const [internal, setInternal] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const suspended = isAccountSuspended(entity);
  const isAdmin = user?.role === "admin";
  useEffect(() => adapter.subscribeWhere("account_suspensions", [["entity_id", "==", entity.id]], (l) => setHistory(l.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))))), [entity.id]);

  const close = () => { setMode(null); setUserMsg(""); setInternal(""); setPw(""); };
  const doSuspend = async () => {
    if (userMsg.trim().length < 5 || internal.trim().length < 5) return toast.error("Both remarks are required (min 5 characters)");
    setBusy(true);
    try { authToast(await suspendAccount({ by: user, kind, entityId: entity.id, uid, phone, userMessage: userMsg.trim(), internalRemark: internal.trim(), prevStatus: entity.status }), "suspended"); close(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const doReinstate = async () => {
    setBusy(true);
    try { authToast(await reinstateAccount({ by: user, kind, entity, uid, phone, password: pw, internalRemark: internal.trim() }), "reinstated"); close(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className={`mt-5 rounded-lg border p-3 ${suspended ? "border-red-200 bg-red-50/50" : "border-border"}`} data-testid="account-suspension">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground flex items-center gap-1.5"><Icons.ShieldBan className="h-4 w-4 text-red-600" /> Account access</p>
          <p className="text-xs text-muted-foreground" data-testid="account-status-text">{suspended ? `Suspended ${fmtTs(entity.suspended_at)}` : "Active"}</p>
        </div>
        {isAdmin && (suspended
          ? <Button size="sm" variant="outline" onClick={() => setMode("reinstate")} data-testid="reinstate-account-btn"><Icons.RotateCcw className="h-3.5 w-3.5 mr-1" /> Reinstate</Button>
          : <Button size="sm" variant="destructive" onClick={() => setMode("suspend")} data-testid="suspend-account-btn"><Icons.Ban className="h-3.5 w-3.5 mr-1" /> Suspend</Button>)}
      </div>
      {suspended && entity.suspension_user_message && <p className="mt-2 text-xs text-red-800" data-testid="suspension-user-msg">User sees: “{entity.suspension_user_message}”</p>}
      {history.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="suspension-history">
          {history.map((h) => (
            <div key={h.id} className="text-[11px] rounded border border-border bg-background p-2">
              <p className="font-mono"><b className={h.action === "suspend" ? "text-red-700" : "text-emerald-700"}>{h.action}</b> · {h.performed_by} · {fmtTs(h.created_at)} · auth {h.auth_enforced ? "enforced" : "not enforced"}</p>
              {h.user_message && <p className="text-muted-foreground">User: {h.user_message}</p>}
              {h.internal_remark && <p className="text-muted-foreground">Internal: {h.internal_remark}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!mode} onOpenChange={(o) => !o && close()}>
        <DialogContent data-testid={`${mode}-account-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">{mode === "suspend" ? `Suspend ${name}` : `Reinstate ${name}`}</DialogTitle>
            <DialogDescription>{mode === "suspend" ? "Blocks sign-in on every Brugo app immediately and revokes active sessions." : "Restores access. Requires your System Control profile password."}</DialogDescription>
          </DialogHeader>
          {mode === "suspend" && (
            <div className="space-y-1.5"><Label>Message shown to the user at sign-in / sign-up *</Label><Textarea value={userMsg} onChange={(e) => setUserMsg(e.target.value)} rows={3} data-testid="suspend-user-msg" placeholder="Your account has been suspended due to… Contact support@brugo.in" /></div>
          )}
          <div className="space-y-1.5"><Label>Internal remark (staff only){mode === "suspend" ? " *" : ""}</Label><Textarea value={internal} onChange={(e) => setInternal(e.target.value)} rows={3} data-testid="suspend-internal-remark" /></div>
          {mode === "reinstate" && <div className="space-y-1.5"><Label>Profile password *</Label><Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reinstate-password" /></div>}
          <DialogFooter>
            <Button variant="ghost" onClick={close} data-testid="suspension-cancel">Cancel</Button>
            {mode === "suspend"
              ? <Button variant="destructive" disabled={busy} onClick={doSuspend} data-testid="suspend-confirm">{busy ? "Suspending…" : "Suspend now"}</Button>
              : <Button disabled={busy || !pw} onClick={doReinstate} data-testid="reinstate-confirm">{busy ? "Reinstating…" : "Reinstate"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
